import { Button, FileButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconFileImport } from "@tabler/icons-react";
import { useCallback, useState } from "react";

import { type ImportError, parseCheckListWorkbook } from "./CheckListImport.utils";
import { type ICheckListForm } from "./CheckLists.types";
import { useChecklistsContext } from "./CheckListsContext";
import CheckListModal from "./CheckListForm/CheckListModal";
import ImportErrorsModal from "./ImportErrorsModal";

const SPREADSHEET_ACCEPT =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

const ImportCheckListButton = () => {
  const { logbooks, checklists } = useChecklistsContext();
  const [editorOpened, { open: openEditor, close: closeEditor }] =
    useDisclosure();
  const [errorsOpened, { open: openErrors, close: closeErrors }] =
    useDisclosure();
  const [parsedForm, setParsedForm] = useState<ICheckListForm | null>(null);
  const [importKey, setImportKey] = useState(0);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [isParsing, setIsParsing] = useState(false);

  const handleFile = useCallback(
    async (file: File | null) => {
      if (!file) return;

      setIsParsing(true);
      try {
        const { form, errors: importErrors } = await parseCheckListWorkbook(
          file,
          logbooks,
        );

        if (form) {
          setParsedForm({ ...form, sort: checklists.length + 1 });
          // Remount CheckListModal so its item state (seeded once on mount) reflects this import.
          setImportKey(prev => prev + 1);
          openEditor();
        } else {
          setErrors(importErrors);
          openErrors();
        }
      } catch (error) {
        console.error("Checklist import failed:", error);
        setErrors([
          {
            field: "file",
            message: `This file could not be read as an Excel spreadsheet. (${String((error as Error)?.message ?? error)})`,
          },
        ]);
        openErrors();
      } finally {
        setIsParsing(false);
      }
    },
    [logbooks, checklists.length, openEditor, openErrors],
  );

  return (
    <>
      <FileButton accept={SPREADSHEET_ACCEPT} onChange={handleFile}>
        {props => (
          <Button
            type="button"
            variant="outline"
            leftSection={<IconFileImport size={16} />}
            loading={isParsing}
            {...props}>
            Import Checklist +
          </Button>
        )}
      </FileButton>

      {parsedForm && (
        <CheckListModal
          key={importKey}
          opened={editorOpened}
          onClose={closeEditor}
          buttonType={parsedForm.type}
          defaultValues={parsedForm as any}
        />
      )}

      <ImportErrorsModal
        opened={errorsOpened}
        onClose={closeErrors}
        errors={errors}
      />
    </>
  );
};

export default ImportCheckListButton;
